import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type {
  CreateRecurringRequest,
  Guid,
  PostRecurringNowResponse,
  RecurringTemplate,
  UpdateRecurringRequest,
} from "@/api/types";

export type RecurringStatus = "loading" | "ready" | "error";

export interface UseRecurringResult {
  /** `null` until resolved and on failure. Always the DEFAULT list — inactive templates
   *  are never included (see this hook's own remarks). */
  templates: RecurringTemplate[] | null;
  status: RecurringStatus;
  error: string | null;
  reload: () => Promise<void>;
  /** Throws on failure — `BooksRecurring.tsx`/`RecurringTemplateDialog.tsx` catch this
   *  themselves to read per-field validation errors off it, which this hook has no
   *  opinion about. */
  create: (body: CreateRecurringRequest) => Promise<RecurringTemplate>;
  /** Also how a template is paused/resumed (`isActive: false/true`) — the row's own
   *  Active switch calls this with just that one field. */
  update: (id: Guid, body: UpdateRecurringRequest) => Promise<RecurringTemplate>;
  /** POST .../post-now — NOT idempotent server-side (`BooksRecurringEndpoints.cs`'s own
   *  remarks): a second back-to-back call posts a second entry rather than refusing. */
  postNow: (id: Guid) => Promise<PostRecurringNowResponse>;
}

/**
 * ef-042-6 — the owner's recurring-transaction templates (`ef-042-4`'s
 * `/api/demo/books/recurring/*`).
 *
 * ALWAYS THE DEFAULT LIST (`includeInactive` omitted/false). An inactive template is
 * meant to disappear from this screen the moment it's toggled off — reload included,
 * `books-recurring.spec.ts`'s own named scenario — and the SERVER is what draws that
 * line (`GET .../recurring?includeInactive=`), never a client-side filter re-deriving
 * the same rule a second time.
 *
 * Re-reads the whole list after every write, same convention `useVendors`/`usePlaces`
 * use and for the same reason: this list is small and a re-read can never drift from
 * what the server holds, including the post-advance `nextDueOn` a `post-now` call
 * leaves behind.
 *
 * Owner-only server-side (403 for an employee login) — this hook does not check that,
 * same reasoning `useVendors`' own remarks give.
 */
export function useRecurring(): UseRecurringResult {
  const [templates, setTemplates] = useState<RecurringTemplate[] | null>(null);
  const [status, setStatus] = useState<RecurringStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const resolved = await demoApi.getRecurring(false, signal);
      if (signal?.aborted) return;
      setTemplates(resolved);
      setStatus("ready");
      setError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setTemplates(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load recurring templates."));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  const create = useCallback(
    async (body: CreateRecurringRequest): Promise<RecurringTemplate> => {
      const template = await demoApi.createRecurring(body);
      await load();
      return template;
    },
    [load],
  );

  const update = useCallback(
    async (id: Guid, body: UpdateRecurringRequest): Promise<RecurringTemplate> => {
      const template = await demoApi.updateRecurring(id, body);
      await load();
      return template;
    },
    [load],
  );

  const postNow = useCallback(
    async (id: Guid): Promise<PostRecurringNowResponse> => {
      const result = await demoApi.postRecurringNow(id);
      await load();
      return result;
    },
    [load],
  );

  return { templates, status, error, reload, create, update, postNow };
}
